import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

export interface SelectionBannerProps {
  selectedCount: number;
  totalCount: number;
  pageCount: number;
  allPageSelected: boolean;
  selectAllMode: boolean;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

const BannerContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.garnetClear};
  border-top: 1px solid ${({ theme }) => theme.colors.greyLight};
  padding: 6px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.greyDark};
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.garnet};
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  font-size: 13px;
  padding: 0;

  &:hover {
    opacity: 0.7;
  }
`;

// Secondary / muted variant for the "clear selection" action, so it doesn't
// read identical to the primary "select all N" offer (same garnet + bold).
const ClearLink = styled(ActionLink)`
  color: ${({ theme }) => theme.colors.greySilver};
  font-weight: 400;
`;

export const SelectionBanner: React.FC<SelectionBannerProps> = ({
  selectedCount,
  totalCount,
  pageCount,
  allPageSelected,
  selectAllMode,
  onSelectAll,
  onClearSelection,
}) => {
  const { t } = useTranslation(['tables']);

  if (selectedCount === 0) return null;

  if (selectAllMode) {
    return (
      <BannerContainer>
        <span>{t('tables:selection.allSelected', { count: selectedCount })}</span>
        <ClearLink onClick={onClearSelection}>
          {t('tables:selection.clearSelection')}
        </ClearLink>
      </BannerContainer>
    );
  }

  // Offer the "select all N matching" escalation only while the whole current
  // page is checked AND there are still more matching rows than already selected.
  const canOfferSelectAll = allPageSelected && selectedCount < totalCount;
  // Only say "N on this page" when the selection IS exactly the current page.
  // Once it spans pages, show the real cumulative count so the banner never
  // under-reports (e.g. 50 selected across pages must not read "25 on this page").
  const exactlyThisPage = allPageSelected && selectedCount === pageCount;

  return (
    <BannerContainer>
      <span>
        {exactlyThisPage && canOfferSelectAll
          ? t('tables:selection.pageSelected', { count: pageCount })
          : t('tables:selection.count', { count: selectedCount })}
      </span>
      {canOfferSelectAll && (
        <ActionLink onClick={onSelectAll}>
          {t('tables:selection.selectAll', { count: totalCount })}
        </ActionLink>
      )}
      <ClearLink onClick={onClearSelection}>
        {t('tables:selection.clearSelection')}
      </ClearLink>
    </BannerContainer>
  );
};
